/**
 * Links to the cabinet itself inside a chat message (docs/cabinet-redesign/02_ЭКРАНЫ_БЫЛО_СТАЛО.md §3.3): a manager can
 * send «/concierge?new=invoice» or the same address in full on this origin, and the client gets a tap target. Only
 * whitelisted cabinet paths become links — external URLs and anything else stay plain text, so a chat message can never
 * carry a phishing link or a javascript: URL into the app.
 */
export type MessagePart = { kind: 'text'; text: string } | { kind: 'link'; text: string; path: string };

const ALLOWED_PATHS = ['/concierge', '/topup', '/card', '/history', '/settings', '/help'];

// A path or a full URL that ends at whitespace or the end of the text.
const CANDIDATE = /(https?:\/\/[^\s]+|\/[a-z][^\s]*)/gi;

function toCabinetPath(raw: string, origin: string): string | null {
  // Trailing punctuation belongs to the sentence, not to the link.
  const trimmed = raw.replace(/[.,;:!?)»"']+$/u, '');
  let url: URL;
  try {
    url = new URL(trimmed, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  const allowed = ALLOWED_PATHS.some(p => url.pathname === p || url.pathname.startsWith(p + '/'));
  return allowed ? url.pathname + url.search : null;
}

export function splitCabinetLinks(text: string, origin: string): MessagePart[] {
  const parts: MessagePart[] = [];
  let last = 0;
  for (const m of text.matchAll(CANDIDATE)) {
    const start = m.index ?? 0;
    // A path only counts at the start of a word — «и/или» is not a link.
    if (m[0].startsWith('/') && start > 0 && !/\s/.test(text[start - 1])) continue;
    const path = toCabinetPath(m[0], origin);
    if (!path) continue;
    const linkText = m[0].replace(/[.,;:!?)»"']+$/u, '');
    if (start > last) parts.push({ kind: 'text', text: text.slice(last, start) });
    parts.push({ kind: 'link', text: linkText, path });
    last = start + linkText.length;
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
  return parts.length ? parts : [{ kind: 'text', text }];
}
